import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import FullPageLayout from 'src/asio-neon-components/Layouts/FullPageLayout';
import { useRouting } from 'src/asio-neon-components/Utils';
import { Button, Tabs, Tab, Stack, Paper, Typography, Box } from '@mui/material';
import { formatMessage } from 'src/i18n/intl';
import ConfigureExternalIdP from 'src/features/authentication-v2/components/ConfigureExternalIdP/ConfigureExternalIdPContainer';
import { EditFlowStyles } from 'src/features/authentication-v2/components/NewAuthentication/NewAuthenticationStyles';
import UsersTable from 'src/features/users-v2/components/NewUsers/NewUsersContainer';
import Locale from 'src/i18n';
import _ from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import { shouldDisableAuthInputs } from 'src/features/authentication-v2/components/utils';

const EditExternalIdP = ({
    onComponentDidMount,
    identityProviderData,
    initialIdentityProviderData,
    loadLoginProviderWithUsers,
    updateLoginProvider,
    updateLoginProviderAssignedUsers,
    usersSelectionUpdated,
    onComponentWillUnmount,
    isError,
    screenKey,
    updatedClientSecretData,
}) => {
    const { scheme } = useParams();
    const { goBack } = useRouting();
    const [selectedTab, setSelectedTab] = useState(0);
    const [readOnly, setReadOnly] = useState(true);
    const [formUpdated, setFormUpdated] = useState(false);

    useEffect(() => {
        onComponentDidMount();
    }, []);

    useEffect(() => {
        return () => onComponentWillUnmount();
    }, []);

    useEffect(() => {
        loadLoginProviderWithUsers(scheme);
    }, [scheme]);

    useEffect(() => {
        const hasUpdatedForm =
            !_.isEqual(initialIdentityProviderData, identityProviderData) ||
            updatedClientSecretData !== 'YOUR_EXISTING_SECRET';
        setFormUpdated(hasUpdatedForm);
    }, [identityProviderData]);

    /* eslint-disable-next-line no-shadow */
    const handleTabChange = useCallback((_, newValue) => setSelectedTab(newValue), []);

    const tabsConfig = [
        {
            label: formatMessage(Locale.authenticationDetails),
            render: () => <ConfigureExternalIdP readOnly={readOnly} screenKey={screenKey} />,
            onSave: updateLoginProvider,
            disabled: !formUpdated,
        },
        {
            label: formatMessage(Locale.usersList),
            render: () => {
                return (
                    <UsersTable
                        embedded
                        readOnly={readOnly}
                        preserveSelection
                        shouldNotShowAlert
                        screenKey={screenKey}
                    />
                );
            },
            onSave: updateLoginProviderAssignedUsers,
            disabled: !usersSelectionUpdated,
        },
    ];

    const actions = (
        <Box px={1} ml="auto">
            <Button
                variant="contained"
                disabled={!readOnly || shouldDisableAuthInputs(selectedTab, screenKey)}
                onClick={() => setReadOnly(prevState => !prevState)}
                endIcon={<EditIcon />}>
                Edit
            </Button>
        </Box>
    );

    const pageTitle = (
        <Typography variant="h4" sx={EditFlowStyles.pageTitle}>
            {!readOnly && formatMessage(Locale.edit)} {initialIdentityProviderData.clientName}
        </Typography>
    );

    return (
        <FullPageLayout PageTitle={pageTitle} actions={actions} screenKey={screenKey}>
            <Stack height="100%" px="16px" boxSizing="border-box">
                {/* Tabs Component */}
                <Stack flexGrow={1}>
                    <Tabs
                        option="primary"
                        value={selectedTab}
                        onChange={handleTabChange}
                        sx={{ width: 'fit-content', px: '12px', my: '16px' }}>
                        {_.map(tabsConfig, ({ label }) => (
                            <Tab option="primary" key={label} label={label} />
                        ))}
                    </Tabs>
                    <Paper
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            flexDirection: 'column',
                            height: '50vh',
                        }}>
                        {tabsConfig[selectedTab || 0]?.render()}
                    </Paper>
                </Stack>

                {/* Footer Component */}

                <Stack py={2}>
                    {!readOnly && (
                        <Paper sx={{ p: 2 }}>
                            <Stack direction="row-reverse" justifyContent="space-between" spacing={1}>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    color="secondary"
                                    onClick={tabsConfig[selectedTab || 0].onSave}
                                    disabled={tabsConfig[selectedTab || 0].disabled || isError}>
                                    {formatMessage(Locale.saveIDP)}
                                </Button>
                                <Button onClick={goBack}>{formatMessage(Locale.cancel)}</Button>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </Stack>
        </FullPageLayout>
    );
};

export default EditExternalIdP;
